import { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { CashoutSettingsPanel } from './CashoutSettingsPanel';
import PendingPayoutsPanel from './PendingPayoutsPanel';
import PaidPayoutsPanel from './PaidPayoutsPanel';
import PlayedBackPanel from './PlayedBackPanel';
import AllCashoutsPanel from '../../components/AllCashoutsPanel';
import CashoutAccountingPanel from './CashoutAccountingPanel';
import PlayItBackDialog from '../../components/PlayItBackDialog';
import PlayBackStatsBadge from '../../components/PlayBackStatsBadge';
import PeerTransfersAdminPanel from './PeerTransfersAdminPanel';
import FraudWatchPanel from './FraudWatchPanel';
import { useShowMore } from '../../components/useShowMore';
import MessageCustomerButton from '../../components/admin/MessageCustomerButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const QUICK_PRESETS = [5, 10, 20, 50];

export const CashoutsAdminTab = ({
    loading,
    cashoutRequests,
    handleCashoutAction,
    formatDate,
    fetchCashoutRequests,
}) => {
    const { visibleItems, ShowMoreButton, total, limit } = useShowMore(cashoutRequests || [], 10, 10);
    const [busy, setBusy] = useState(false);
    const [quickBusyId, setQuickBusyId] = useState(null);
    const [dialogReq, setDialogReq] = useState(null);
    const [playBackStats, setPlayBackStats] = useState({});

    // Fetch per-player play-back stats for the visible rows so we can show a
    // responsible-play badge (e.g. "$40 / 7d · 8× · 6 today").
    useEffect(() => {
        const ids = [...new Set((cashoutRequests || []).map((r) => r.user_id).filter(Boolean))];
        if (ids.length === 0) { setPlayBackStats({}); return; }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.post(
                    `${API}/admin/play-back-stats`,
                    { user_ids: ids },
                    { withCredentials: true }
                );
                if (!cancelled) setPlayBackStats(data?.stats || {});
            } catch { /* badge is non-critical */ }
        })();
        return () => { cancelled = true; };
    }, [cashoutRequests]);

    const openDialog = (req) => setDialogReq(req);
    const closeDialog = (open) => { if (!open) setDialogReq(null); };

    const fireQuickPlayBack = async (req, amount) => {
        setQuickBusyId(`${req.id}-${amount ?? 'all'}`);
        try {
            const body = { transaction_id: req.id };
            if (amount != null) body.amount = amount;
            const { data } = await axios.post(
                `${API}/admin/cashout-play-back`,
                body,
                { withCredentials: true }
            );
            toast.success(
                (data.partial
                    ? `Played back $${data.refunded.toFixed(2)} — $${data.remaining_cashout.toFixed(2)} still pending`
                    : `Played back $${data.refunded.toFixed(2)} for ${req.user_name}`) +
                (data.bonus_granted ? ` · +$${data.bonus_granted.toFixed(2)} load bonus` : '')
            );
            await fetchCashoutRequests?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to play back');
        }
        setQuickBusyId(null);
    };

    const handleConfirm = async ({ amount, note }) => {
        if (!dialogReq) return;
        setBusy(true);
        try {
            const body = { transaction_id: dialogReq.id };
            if (amount != null) body.amount = amount;
            if (note) body.note = note;
            const { data } = await axios.post(
                `${API}/admin/cashout-play-back`,
                body,
                { withCredentials: true }
            );
            toast.success(
                (data.partial
                    ? `Played back $${data.refunded.toFixed(2)} — $${data.remaining_cashout.toFixed(2)} still pending`
                    : `Played back $${data.refunded.toFixed(2)} for ${dialogReq.user_name}`) +
                (data.bonus_granted ? ` · +$${data.bonus_granted.toFixed(2)} load bonus` : '')
            );
            setDialogReq(null);
            await fetchCashoutRequests?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to play back');
        }
        setBusy(false);
    };

    return (
    <div id="cashouts-tab-root">
        <CashoutAccountingPanel />
        <CashoutSettingsPanel />
        <PendingPayoutsPanel onChanged={fetchCashoutRequests} />
        <AllCashoutsPanel role="admin" />
        <PaidPayoutsPanel />
        <PlayedBackPanel />
        <PeerTransfersAdminPanel />
        <FraudWatchPanel />
        <div className="card-dark p-6">
            <h3 className="text-base font-bold text-white mb-4">Cashout Requests · Pending Approval</h3>
            {loading ? (
                <div className="flex justify-center py-12"><div className="spinner" /></div>
            ) : (cashoutRequests || []).length === 0 ? (
                <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-[#39FF14] mx-auto mb-4" />
                    <p className="text-[#A0AAB2]">No pending cashout requests</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-white/10">
                                <TableHead className="text-[#A0AAB2]">User</TableHead>
                                <TableHead className="text-[#A0AAB2]">Email</TableHead>
                                <TableHead className="text-[#A0AAB2] text-right">Amount</TableHead>
                                <TableHead className="text-[#A0AAB2]">Payout Method</TableHead>
                                <TableHead className="text-[#A0AAB2]">Handle / Send To</TableHead>
                                <TableHead className="text-[#A0AAB2]">Date</TableHead>
                                <TableHead className="text-[#A0AAB2] text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {visibleItems.map((req, i) => (
                                <TableRow key={i} className="border-white/10">
                                    <TableCell className="text-white font-medium">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span>{req.user_name}</span>
                                            <PlayBackStatsBadge stats={playBackStats[req.user_id]} userId={req.user_id} />
                                            {req.operator_name && (
                                                <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded border ${
                                                    req.operator_is_house
                                                        ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
                                                        : 'bg-sky-500/10 text-sky-300 border-sky-500/30'
                                                }`}
                                                    title={req.operator_is_house ? 'Your house pool' : 'External storefront — you may want to delegate'}>
                                                    {req.operator_name}
                                                </span>
                                            )}
                                        </div>
                                        {req.user_phone && (
                                            <div className="text-[10px] text-[#666] font-mono mt-0.5">{req.user_phone}</div>
                                        )}
                                        <div className="mt-1">
                                            <MessageCustomerButton userId={req.user_id} userName={req.user_name} />
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-[#A0AAB2]">{req.user_email}</TableCell>
                                    <TableCell className="text-right font-mono text-[#FF007F]">
                                        <div className="font-bold">
                                            ${(req.net_amount != null
                                                ? Number(req.net_amount)
                                                : (Math.abs(Number(req.amount || 0)) - Number(req.fee_amount || 0))
                                            ).toFixed(2)}
                                        </div>
                                        {Number(req.fee_amount || 0) > 0 && (
                                            <div className="text-[9px] text-[#A0AAB2] font-normal">
                                                ${Math.abs(Number(req.amount || 0)).toFixed(2)} − ${Number(req.fee_amount || 0).toFixed(2)} fee
                                            </div>
                                        )}
                                        {req.large_payout && (
                                            <div>
                                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#FF007F]/15 text-[#FF007F] border border-[#FF007F]/40">
                                                    LARGE · 48hr
                                                </span>
                                            </div>
                                        )}
                                        {Number(req.partial_play_back_total) > 0 && (
                                            <div className="text-[9px] text-[#A0AAB2] font-mono font-normal mt-0.5 leading-tight"
                                                 data-testid={`cashout-playback-history-${i}`}
                                                 title="Original cashout · played back · still owed">
                                                <span className="text-white">${Number(req.original_amount != null ? req.original_amount : Math.abs(Number(req.amount || 0)) + Number(req.partial_play_back_total || 0)).toFixed(2)}</span> cashout
                                                <span className="text-[#00E0FF]"> · ${Number(req.partial_play_back_total).toFixed(2)} back</span>
                                                <span className="text-[#FFD700]"> · ${(req.status === 'played_back' ? 0 : Math.abs(Number(req.amount || 0))).toFixed(2)} owed</span>
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {req.payout_method ? (
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/30">
                                                {req.payout_method}
                                            </span>
                                        ) : (
                                            <span className="text-[#666] text-xs italic">legacy</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {req.payout_handle ? (
                                            <button
                                                type="button"
                                                onClick={() => { navigator.clipboard?.writeText(req.payout_handle); toast.success('Handle copied'); }}
                                                className="font-mono text-xs text-white hover:text-[#FFD700] transition-colors"
                                                title="Click to copy"
                                                data-testid={`cashout-handle-${i}`}
                                            >
                                                {req.payout_handle}
                                            </button>
                                        ) : (
                                            <span className="text-[#666] text-xs">—</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-[#A0AAB2]">{formatDate(req.created_at)}</TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-2 flex-wrap">
                                            <Button size="sm" onClick={() => handleCashoutAction(req.id, 'approve')}
                                                className="bg-[#39FF14] text-black hover:bg-[#39FF14]/80"
                                                data-testid={`cashout-approve-${i}`}>
                                                <CheckCircle className="w-4 h-4 mr-1" />Approve
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => openDialog(req)}
                                                className="border-[#00E0FF]/40 text-[#00E0FF] hover:bg-[#00E0FF]/10"
                                                data-testid={`cashout-play-back-${i}`}
                                                title="Refund credits to player — supports partial amount"
                                            >
                                                <RefreshCcw className="w-4 h-4 mr-1" />Play It Back
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => handleCashoutAction(req.id, 'reject')}
                                                className="border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10"
                                                data-testid={`cashout-reject-${i}`}>
                                                <XCircle className="w-4 h-4 mr-1" />Reject
                                            </Button>
                                        </div>
                                        {(() => {
                                            const remaining = Math.abs(req.amount || 0);
                                            const presets = QUICK_PRESETS.filter((p) => p < remaining);
                                            if (remaining <= 0) return null;
                                            return (
                                                <div className="flex items-center justify-end gap-1.5 mt-2 flex-wrap" data-testid={`quick-playback-row-${i}`}>
                                                    <span className="text-[9px] uppercase tracking-wider text-[#A0AAB2] font-bold">Quick play-back:</span>
                                                    {presets.map((p) => {
                                                        const key = `${req.id}-${p}`;
                                                        return (
                                                            <button
                                                                type="button"
                                                                key={p}
                                                                onClick={() => fireQuickPlayBack(req, p)}
                                                                disabled={quickBusyId === key}
                                                                className="px-2.5 py-1 rounded-full text-[11px] font-black bg-[#00E0FF]/10 text-[#00E0FF] border border-[#00E0FF]/30 hover:bg-[#00E0FF]/25 disabled:opacity-50 transition-colors"
                                                                data-testid={`quick-playback-${i}-${p}`}
                                                            >
                                                                ${p}
                                                            </button>
                                                        );
                                                    })}
                                                    <button
                                                        type="button"
                                                        onClick={() => fireQuickPlayBack(req, null)}
                                                        disabled={quickBusyId === `${req.id}-all`}
                                                        className="px-2.5 py-1 rounded-full text-[11px] font-black bg-[#00E0FF]/25 text-white border border-[#00E0FF] hover:bg-[#00E0FF]/40 disabled:opacity-50 transition-colors"
                                                        data-testid={`quick-playback-${i}-all`}
                                                    >
                                                        All ${remaining.toFixed(0)}
                                                    </button>
                                                </div>
                                            );
                                        })()}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
            {(cashoutRequests || []).length > 0 && (
                <>
                    <p className="text-[10px] text-[#A0AAB2] text-center mt-2">
                        Showing {Math.min(limit, total)} of {total}
                    </p>
                    <ShowMoreButton testid="cashout-requests-show-more" />
                </>
            )}
        </div>

        <PlayItBackDialog
            open={!!dialogReq}
            onOpenChange={closeDialog}
            maxAmount={dialogReq ? Math.abs(dialogReq.amount || 0) : 0}
            playerName={dialogReq?.user_name}
            isAdmin={true}
            busy={busy}
            onConfirm={handleConfirm}
        />
    </div>
    );
};
